<template>
  <v-navigation-drawer
    v-model="$store.state.drawer"
    disable-route-watcher
    persistent
    overflow
    fixed
    app
  >
    <!-- Logged in user display -->
    <v-toolbar flat class="transparent">
      <v-list class="pa-0" two-line v-if="loggedIn">
        <v-list-tile avatar>
          <v-list-tile-avatar>
            <img :src="user.avatar" class="elevation-2" />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ fullName }}</v-list-tile-title>
            <v-list-tile-sub-title v-if="role">{{ role.capitalize() }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </v-toolbar>

    <v-list class="pt-0" dense>
      <v-divider />

      <!-- Home -->
      <v-list-tile key="home" to="/" exact @click="toggleDrawerIfXs">
        <v-list-tile-action> <v-icon>mdi-home</v-icon> </v-list-tile-action
        ><v-list-tile-content>
          <v-list-tile-title>Home</v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>

      <!-- Dashboard -->
      <v-list-tile key="dashboard" to="/dashboard" @click="toggleDrawerIfXs">
        <v-list-tile-action> <v-icon>mdi-view-dashboard</v-icon> </v-list-tile-action
        ><v-list-tile-content>
          <v-list-tile-title>Dashboard</v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>

      <v-divider />

      <!-- API Explorer -->
      <can I="read" a="Api">
        <v-list-tile key="api" to="/api" exact @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-developer-board</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>API Explorer</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Analytics -->
      <can I="read" a="Analytics">
        <v-list-tile key="analytics" to="/analytics" exact @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-google-analytics</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Analytics</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Administrators -->
      <can I="list" a="Administrator">
        <v-list-tile key="administrators" to="/administrators" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-account-key</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Administrators</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Applications -->
      <can I="list" a="OpportunityApplication">
        <v-list-tile key="applications" to="/applications" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-clipboard-account</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Applications</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Attachments -->
      <can I="list" a="Attachment">
        <v-list-tile key="attachments" to="/attachments" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-file-document</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Attachments</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Bundle Errors -->
      <can I="list" a="Attachment">
        <v-list-tile key="bundle-errors" to="/tools/bundle_errors" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-file-document</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Bundle Errors</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Campaigns -->
      <can I="list" a="Campaign">
        <v-list-tile key="campaigns" to="/campaigns" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-clock-start</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Campaigns</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Candidates -->
      <can I="list" a="Candidate">
        <v-list-tile key="candidates" to="/candidates" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-account</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Candidates</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Clients -->
      <can I="list" a="Client">
        <v-list-tile key="clients" to="/clients" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-city</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Clients</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Disciplines -->
      <can I="list" a="Discipline">
        <v-list-tile key="disciplines" to="/disciplines" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-google-circles-extended</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Disciplines</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Document Bundles -->
      <can I="list" a="DocumentBundle">
        <v-list-tile key="document-bundles" to="/document_bundles" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-file-pdf-box</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Document Bundles</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Eligibilities -->
      <can I="read" a="Eligibility">
        <v-list-tile key="eligibilities" to="/eligibilities" @click="toggleDrawerIfXs">
          <v-list-tile-action>
            <v-icon>mdi-checkbox-marked-circle-outline</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Eligibilities</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Experiences -->
      <can I="list" a="Experience">
        <v-list-tile key="experiences" to="/experiences" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-podcast</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Experiences</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Organization Experiences -->
      <can I="list" a="ExperienceOrganization">
        <v-list-tile
          key="experience_organizations"
          to="/experience_organizations"
          @click="toggleDrawerIfXs"
        >
          <v-list-tile-action> <v-icon>mdi-city-variant</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Experience Organizations</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Flags -->
      <can I="list" a="Flag">
        <v-list-tile key="flags" to="/flags" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-flag</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Flags</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Institutions -->
      <can I="list" a="Institution">
        <v-list-tile key="institutions" to="/institutions" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-school</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Institutions</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Invites -->
      <can I="list" a="Invite">
        <v-list-tile key="invites" to="/invites" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-video-input-antenna</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Invites</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Jobs -->
      <can I="list" a="Job">
        <v-list-tile key="jobs" to="/jobs" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-history</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Jobs</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Opportunities -->
      <can I="list" a="Opportunity">
        <v-list-tile key="opportunities" to="/opportunities" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-worker</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Opportunities</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Organizations -->
      <can I="list" a="Organization">
        <v-list-tile key="organizations" to="/organizations" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-domain</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Organizations</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Profiles -->
      <can I="list" a="Profile">
        <v-list-tile key="profiles" to="/profiles" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-account-card-details</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Profiles</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Profile Qualifications -->
      <can I="read" a="ProfileQualification">
        <v-list-tile
          key="profile-qualifications"
          to="/profile_qualifications"
          @click="toggleDrawerIfXs"
        >
          <v-list-tile-action> <v-icon>mdi-decagram</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Profile Qualifications</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Qualifications -->
      <can I="list" a="Qualification">
        <v-list-tile key="qualifications" to="/qualifications" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-book-open-page-variant</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Qualifications</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Qualification Types -->
      <can I="list" a="QualificationType">
        <v-list-tile key="qualification_types" to="/qualification_types" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-folder-open</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Qualification Types</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Referrals -->
      <can I="list" a="Referral">
        <v-list-tile key="referrals" to="/referrals" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-account-star</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Referrals</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Reports -->
      <can I="list" a="Report">
        <v-list-tile key="reports" to="/reports" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-file-chart-outline</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Reports</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Support Activity -->
      <can I="list" a="HourlyAnalytic">
        <v-list-tile key="support-activity" to="/tools/activity" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-chart-bar-stacked</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Support Activity</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Verifications -->
      <can I="verify" a="ProfileQualification">
        <v-list-tile key="verifications" to="/tools/verification" @click="toggleDrawerIfXs">
          <v-list-tile-action> <v-icon>mdi-check</v-icon> </v-list-tile-action
          ><v-list-tile-content>
            <v-list-tile-title>Verification</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </can>

      <!-- Log out -->
      <v-divider></v-divider>
      <v-list-tile @click.stop="logOut">
        <v-list-tile-action>
          <v-icon>mdi-logout</v-icon>
        </v-list-tile-action>
        <v-list-tile-content>
          <v-list-tile-title>Log out</v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  computed: {
    ...mapGetters({
      loggedIn: 'auth/loggedIn',
      fullName: 'auth/fullName',
      user: 'auth/user',
      role: 'auth/role',
    }),
  },
  methods: {
    toggleDrawerIfXs() {
      if (this.$vuetify.breakpoint.xsOnly) this.$store.commit('closeDrawer')
    },
    logOut() {
      this.$store.dispatch('auth/logOut')
      this.$store.commit('closeDrawer')
      this.$router.push('/')
    },
  },
}
</script>
